/**
 * Fantasy Console 0 - Status Bar Component
 */

import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useCart } from '../../context/CartContext'
import { useTheme } from '../../context/ThemeContext'
import { formatTimeAgo } from '../../utils/authErrors'
import { TABS } from '../../types'
import { ThemeModal } from '../Modals/ThemeModal'
import styles from './StatusBar.module.css'
import { CloudUploadIcon, SunIcon, MoonIcon } from '../shared/icons'

interface StatusBarProps {
  onShowShortcuts?: () => void
}

export function StatusBar({ onShowShortcuts }: StatusBarProps) {
  const { cart, saveStatus, activeTab } = useCart()
  const activeTabInfo = TABS.find((t) => t.id === activeTab)

  return (
    <footer className={styles.statusBar}>
      <div className={styles.left}>
        <span className={styles.tab}>{activeTabInfo?.label}</span>
        <span className={styles.separator}>|</span>
        <span className={styles.title}>{cart.metadata.title}</span>
      </div>

      <div className={styles.right}>
        <LocalSaveStatusIndicator status={saveStatus} />
        <CloudSaveStatusIndicator />
        <ThemeToggle />
        <button
          className={styles.hint}
          onClick={onShowShortcuts}
          title='Show keyboard shortcuts'
        >
          <kbd>Ctrl + ?</kbd> Shortcuts
        </button>
      </div>
    </footer>
  )
}

function CloudSaveStatusIndicator() {
  const { cart, cloudSaveEnabled, hasUnsavedCloudChanges, lastCloudSaveTime } =
    useCart()
  const { user } = useAuth()

  const isCartOwner = !cart.userId || (user && cart.userId === user.id)
  const neverSavedToCloud = user && !cart.id

  if (!cloudSaveEnabled) return null

  if (!user) {
    return (
      <span className={`${styles.saveStatus} ${styles.statusMuted}`}>
        <CloudUploadIcon />
        Sign in to save to cloud
      </span>
    )
  }

  if (!isCartOwner) {
    return (
      <span className={`${styles.saveStatus} ${styles.statusMuted}`}>
        <CloudUploadIcon />
        Fork to save to cloud
      </span>
    )
  }

  if (neverSavedToCloud) {
    return (
      <span className={`${styles.saveStatus} ${styles.statusMuted}`}>
        <CloudUploadIcon />
        Not saved to cloud
      </span>
    )
  }

  if (hasUnsavedCloudChanges) {
    return (
      <span className={`${styles.saveStatus} ${styles.statusWarning}`}>
        <CloudUploadIcon />
        Unsaved cloud changes
      </span>
    )
  }

  if (lastCloudSaveTime) {
    return (
      <span className={`${styles.saveStatus} ${styles.statusSuccess}`}>
        <CheckIcon />
        Cloud saved {formatTimeAgo(lastCloudSaveTime)}
      </span>
    )
  }

  return (
    <span className={`${styles.saveStatus} ${styles.statusSuccess}`}>
      <CheckIcon />
      Synced
    </span>
  )
}

function LocalSaveStatusIndicator({
  status,
}: {
  status: 'saved' | 'saving' | 'unsaved'
}) {
  if (status === 'saving') {
    return (
      <span className={`${styles.saveStatus} ${styles.saving}`}>
        <div className={styles.savingDot} />
        Saving...
      </span>
    )
  }

  if (status === 'unsaved') {
    return (
      <span className={`${styles.saveStatus} ${styles.unsaved}`}>
        Unsaved changes
      </span>
    )
  }

  return (
    <span className={`${styles.saveStatus} ${styles.saved}`}>
      <CheckIcon />
      Saved Locally
    </span>
  )
}

function ThemeToggle() {
  const { theme } = useTheme()
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <button
        className={styles.themeToggle}
        onClick={() => setIsModalOpen(true)}
        title='Change theme'
      >
        {theme.isDark ? <SunIcon /> : <MoonIcon />}
      </button>
      <ThemeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  )
}

function CheckIcon() {
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M2 6 l 3 3 6 -6' />
    </svg>
  )
}
